import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from '../store/Store'
import {contractApi} from './Contract.api'
import {IContractDto} from './Contract.model'
import { IFilterDto, TSort } from '../../common/model/Core.model'
import { signOut } from '../auth/Auth.slice'

type InitialState = {
  filtersSelection: IFilterDto[]
  contracts: {
    error?: string
    loading: boolean
    data: IContractDto[]
    pagination?: {
      limit?: number
      sort?: TSort
      page?: string | undefined
    }
    filters?: IFilterDto[]
  }
  contract: {
    error?: string
    loading: boolean
    data?: IContractDto
  }
}
const initialState = {
  filtersSelection: [],
  contracts: {
    error: undefined,
    loading: false,
    data: [],
    pagination: {
      limit: 25,
      sort: 'ASC',
      page: undefined,
    },
    filters: undefined,
  },
  contract: {
    error: undefined,
    loading: false,
    data: undefined,
  },
} as InitialState

export const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    setContractFiltersSelection: (state, action: PayloadAction<IFilterDto[]>) => {
      state.filtersSelection = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(signOut, () => initialState)
      //Search
      .addMatcher(contractApi.endpoints.contractSearch.matchPending, (state) => {
        return {
          ...state,
          contracts: {
            ...initialState.contracts,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractSearch.matchFulfilled, (state, action) => {
        return {
          ...state,
          contracts: {
            ...state.contracts,
            error: undefined,
            loading: false,
            data: action.payload.data,
            pagination: action.payload.pagination,
            filters: action.payload.filters,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractSearch.matchRejected, (state, action) => {
        return {
          ...state,
          contracts: {
            ...state.contracts,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //LoadMore
      .addMatcher(contractApi.endpoints.contractLoadMore.matchPending, (state) => {
        return {
          ...state,
          contracts: {
            ...state.contracts,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractLoadMore.matchFulfilled, (state, action) => {
        return {
          ...state,
          contracts: {
            ...state.contracts,
            error: undefined,
            loading: false,
            data: state.contracts.data.concat(action.payload.data),
            pagination: action.payload.pagination,
            filters: action.payload.filters,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractLoadMore.matchRejected, (state, action) => {
        return {
          ...state,
          contracts: {
            ...state.contracts,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //GetById
      .addMatcher(contractApi.endpoints.contractGetById.matchPending, (state) => {
        return {
          ...state,
          contract: {
            ...initialState.contract,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractGetById.matchFulfilled, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: undefined,
            loading: false,
            data: action.payload,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractGetById.matchRejected, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //Create
      .addMatcher(contractApi.endpoints.contractCreate.matchPending, (state) => {
        return {
          ...state,
          contract: {
            ...initialState.contract,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractCreate.matchFulfilled, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: undefined,
            loading: false,
            data: action.payload,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractCreate.matchRejected, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //DeleteById
      .addMatcher(contractApi.endpoints.contractDeleteById.matchPending, (state) => {
        return {
          ...state,
          contract: {
            ...initialState.contract,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractDeleteById.matchFulfilled, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: undefined,
            loading: false,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractDeleteById.matchRejected, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //UpdateById
      .addMatcher(contractApi.endpoints.contractUpdateById.matchPending, (state) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractUpdateById.matchFulfilled, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: undefined,
            loading: false,
            data: action.payload,
          },
        }
      })
      .addMatcher(contractApi.endpoints.contractUpdateById.matchRejected, (state, action) => {
        return {
          ...state,
          contract: {
            ...state.contract,
            error: action.error.message,
            loading: false,
          },
        }
      })
  },
})

export const {setContractFiltersSelection} = contractSlice.actions
export default contractSlice.reducer
export const selectContractsError = (state: RootState) => state.contractReducer.contracts.error
export const selectContractsLoading = (state: RootState) => state.contractReducer.contracts.loading
export const selectContracts = (state: RootState) => state.contractReducer.contracts.data
export const selectContractsPagination = (state: RootState) => state.contractReducer.contracts.pagination
export const selectContractsFilters = (state: RootState) => state.contractReducer.contracts.filters
export const selectContractFiltersSelection = (state: RootState) => state.contractReducer.filtersSelection

export const selectContractError = (state: RootState) => state.contractReducer.contract.error
export const selectContractLoading = (state: RootState) => state.contractReducer.contract.loading
export const selectContract = (state: RootState) => state.contractReducer.contract.data
